import React from "react";
import { Link } from "@inertiajs/react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../../constants/styles";

const EXAM_LABELS = {
  not_taken: { label: "Belum Ujian", tone: "muted" },
  passed:    { label: "Lulus Ujian", tone: "success" },
  failed:    { label: "Belum Lulus Ujian", tone: "danger" },
  remedial_needed: { label: "Perlu Remedial", tone: "danger" },
};

function CourseCurriculumList({ curriculums = [], comingSoon = false }) {
  if (!curriculums.length) return null;
  return (
    <Section aria-labelledby="course-curriculum-list">
      <Inner>
        <Title id="course-curriculum-list">Modul</Title>
        <Subtitle>
          {comingSoon
            ? "Materi sedang disiapkan. Pantau halaman ini untuk pembukaan akses."
            : "Pilih modul di bawah untuk mulai belajar."}
        </Subtitle>
        <Stack>
          {curriculums.map((cur) => (
            <Row key={cur.id}>
              <Thumb className="thumb-area">
                {cur.banner_image_url ? (
                  <ThumbImg src={cur.banner_image_url} alt={cur.name} loading="lazy" />
                ) : (
                  <ThumbPlaceholder aria-hidden="true">{cur.name?.[0] || "K"}</ThumbPlaceholder>
                )}
              </Thumb>
              <Content className="content-area">
                <Name>{cur.name}</Name>
                <Meta>
                  {cur.lesson_count || 0} materi
                  {cur.exam_available ? " • Tersedia ujian" : ""}
                </Meta>
                {cur.description && <Desc>{cur.description}</Desc>}
                {typeof cur.progress_percent === "number" && (
                  <Progress>
                    <ProgressBar
                      role="progressbar"
                      aria-valuenow={cur.progress_percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <ProgressFill style={{ width: `${cur.progress_percent}%` }} />
                    </ProgressBar>
                    <ProgressText>{cur.progress_percent}%</ProgressText>
                  </Progress>
                )}
                <PillRow>
                  <ExamPill $tone={EXAM_LABELS[cur.exam_status]?.tone || "muted"}>
                    {EXAM_LABELS[cur.exam_status]?.label || "Belum Ujian"}
                  </ExamPill>
                </PillRow>
              </Content>
              <Cta className="cta-area">
                {comingSoon ? (
                  <ComingSoonPill>Segera Hadir</ComingSoonPill>
                ) : (
                  <PrimaryLink href={cur.detail_url} aria-label={`Pelajari ${cur.name}`}>
                    <span>Pelajari</span>
                    <ChevronIcon aria-hidden="true" viewBox="0 0 24 24">
                      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </ChevronIcon>
                  </PrimaryLink>
                )}
              </Cta>
            </Row>
          ))}
        </Stack>
      </Inner>
    </Section>
  );
}

export default React.memo(CourseCurriculumList);

const Section = styled.section`
  background: #ffffff;
  padding: 64px 0;
  [data-theme="dark"] & { background: #0f172a; }

  @media ${narrowScreenMediaQuery} { padding: 36px 0; }
`;

const Inner = styled.div`
  ${outerContainerCss}
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 6px;
  letter-spacing: -0.005em;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const Subtitle = styled.p`
  margin: 0 0 28px;
  color: #6b7280;
  font-size: 15px;
  [data-theme="dark"] & { color: #9ca3af; }
`;

const Stack = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 168px 1fr auto;
  gap: 22px;
  align-items: center;
  padding: 22px 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${card_shadow_md};
    border-color: rgba(216, 89, 7, 0.32);
  }

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 96px 1fr;
    grid-template-areas:
      "thumb content"
      "cta   cta";
    gap: 14px;
    padding: 16px;

    & > .thumb-area   { grid-area: thumb; }
    & > .content-area { grid-area: content; }
    & > .cta-area     { grid-area: cta; }
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const Thumb = styled.div`
  aspect-ratio: 16 / 10;
  background: linear-gradient(135deg, #fcd34d, #fb923c);
  border-radius: 12px;
  overflow: hidden;
  position: relative;
`;

const ThumbImg = styled.img`
  position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block;
`;

const ThumbPlaceholder = styled.div`
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-size: 36px; font-weight: 800; color: rgba(255,255,255,0.85);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const Name = styled.h3`
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  color: #1f2937;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const Meta = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  [data-theme="dark"] & { color: #9ca3af; }
`;

const Desc = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #4b5563;
  [data-theme="dark"] & { color: #cbd5e1; }
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: rgba(0,0,0,0.08);
  overflow: hidden;
  [data-theme="dark"] & { background: rgba(255,255,255,0.12); }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${brand_gradient};
`;

const ProgressText = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  font-weight: 700;
  color: ${brand_orange_deep};
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const ExamPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ $tone }) =>
    $tone === "success" ? "rgba(16,185,129,0.12)" :
    $tone === "danger"  ? "rgba(220,38,38,0.12)"  :
                          "rgba(107,114,128,0.12)"};
  color: ${({ $tone }) =>
    $tone === "success" ? "#047857" :
    $tone === "danger"  ? "#b91c1c" :
                          "#4b5563"};
`;

const Cta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media ${narrowScreenMediaQuery} { justify-content: flex-start; }
`;

const ComingSoonPill = styled.span`
  display: inline-block;
  padding: 10px 22px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  color: #6b7280;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: not-allowed;
`;

const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
  transition: filter 150ms ease, transform 100ms ease, gap 200ms ease;

  &:hover { filter: brightness(0.95); transform: translateY(-1px); gap: 12px; }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
`;

const ChevronIcon = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;
